/**
 * Vollbild am Handy (Rueckmeldung vom 26.09.2026).
 *
 * Im Browser eines Handys frisst die Adressleiste oben rund ein Viertel der
 * Hoehe, und die Gestenleiste von Android legt sich im Querformat ueber den
 * rechten Rand. Uebrig bleibt ein sehr flacher Streifen - darin geht jede
 * Bedienoberflaeche in die Knie.
 *
 * Vollbild loest beides auf einmal: Adressleiste weg, Gestenleiste weg.
 *
 * Wichtig: Browser erlauben Vollbild NUR aus einer Eingabe heraus. Beim
 * Oeffnen der Seite laesst es sich also nicht anfragen.
 *
 * SEIT DEM 15.10.2026 beim ERSTEN ANTIPPEN (Rueckmeldung aus dem Spiel: der
 * Fullscreen soll schon im Menue da sein, nicht erst im Match). Die erste
 * Beruehrung ist der fruehestmoegliche erlaubte Augenblick - und die
 * passiert im Menue ohnehin sofort. Siehe vollbildBeimErstenTipp unten.
 *
 * Gehorcht wird dabei dem KLICK und nicht dem Aufsetzen des Fingers: Bei
 * Beruehrungen zaehlt als Eingabe erst das Loslassen. Mit "pointerdown"
 * passierte am Handy gar nichts (Rueckmeldung vom 15.10.2026).
 *
 * AUF DEM IPHONE GIBT ES KEIN VOLLBILD (nachgeprueft am 15.10.2026).
 *
 * Safari erlaubt es dort nur fuer Videos, nicht fuer eine Seite - und das
 * gilt fuer jeden Browser auf dem iPhone, denn alle benutzen dieselbe
 * Technik darunter. Auf dem iPad geht es seit iPadOS 16.4, auf Android
 * ohnehin. Gemeldet hat es Robin: "Auf Safari am Handy wechselt man weder
 * automatisch in den Fullscreen noch ueber den Knopf."
 *
 * Dort hilft nur "Zum Home-Bildschirm hinzufuegen": Von dort gestartet
 * laeuft die Seite ohne Leisten - siehe startbildschirm.ts. Damit kein
 * toter Knopf stehen bleibt, fragt die Oberflaeche vorher
 * vollbildMoeglich().
 */

/** Ist gerade Vollbild aktiv? */
export function isFullscreen(): boolean {
  return typeof document !== 'undefined' && document.fullscreenElement !== null;
}

/**
 * Kann dieser Browser ueberhaupt Vollbild?
 *
 * Gefragt wird genau das, was enterFullscreen benutzt - nicht mehr und
 * nicht weniger. Eine Antwort, die davon abweicht, waere schlimmer als
 * keine: Entweder bliebe ein Knopf stehen, der nichts tut (iPhone), oder
 * wir versteckten ihn auf einem Geraet, das es gekonnt haette.
 *
 * document.fullscreenEnabled ist auf dem iPhone false, auf dem iPad seit
 * iPadOS 16.4 true, auf Android true. Es ist auch dann false, wenn eine
 * Seite in einem Rahmen (iframe) ohne Erlaubnis steckt - auch das ist
 * richtig so, denn dort ginge es wirklich nicht.
 */
export function vollbildMoeglich(): boolean {
  if (typeof document === 'undefined') return false;
  if (typeof document.documentElement.requestFullscreen !== 'function') return false;
  return document.fullscreenEnabled;
}

/**
 * Laeuft das Spiel schon vom Startbildschirm, also ohne Browserleisten?
 *
 * Zwei Wege, weil Apple einen eigenen geht: display-mode kennen alle
 * modernen Browser, navigator.standalone nur Safari auf iOS.
 */
export function laeuftAlsApp(): boolean {
  if (typeof window === 'undefined') return false;

  const apple = (navigator as Navigator & { standalone?: boolean }).standalone;
  if (apple === true) return true;

  return window.matchMedia?.('(display-mode: standalone)').matches ?? false;
}

/**
 * Ein Geraet, das mit dem Finger bedient wird?
 *
 * Nur dort wird das Vollbild von selbst angefragt. Am Rechner waere es eine
 * Ueberraschung - dort gibt es den Knopf.
 */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

/**
 * Gibt es an diesem Geraet einen genauen Zeiger - also eine Maus oder ein
 * Trackpad (Produktentscheidung vom 25.10.2026)?
 *
 * Gefragt wird die EINGABE und nicht das Geraet: Ein Laptop mit Touchscreen
 * ist beides, ein Tablet mit Tastatur auch. Deshalb "any-pointer" - es
 * genuegt, dass irgendein genauer Zeiger da ist, auch wenn gerade der Finger
 * benutzt wird.
 *
 * Im Zweifel JA: Lieber steht der Umschalter da und wird nicht gebraucht,
 * als dass jemand am Computer ihn nirgends findet.
 */
export function mausVorhanden(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return window.matchMedia('(any-pointer: fine)').matches;
}

/** Geht ins Vollbild und legt das Querformat fest, soweit das Geraet es kann. */
export async function enterFullscreen(): Promise<void> {
  if (typeof document === 'undefined' || isFullscreen()) return;

  try {
    await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
  } catch {
    // Nicht moeglich - kein Grund, das Spiel aufzuhalten.
    return;
  }

  /*
   * Die Drehsperre geht nur IM Vollbild und nur auf Android. Sie verhindert,
   * dass das Bild beim Hinlegen des Geraets ins Hochformat kippt.
   */
  try {
    const orientation = screen.orientation as ScreenOrientation & {
      lock?: (kind: string) => Promise<void>;
    };
    await orientation.lock?.('landscape');
  } catch {
    // Kein Grund zur Sorge: Der Querformat-Hinweis faengt den Fall ab.
  }
}

/** Verlaesst das Vollbild wieder. */
export async function exitFullscreen(): Promise<void> {
  if (typeof document === 'undefined' || !isFullscreen()) return;
  try {
    await document.exitFullscreen();
  } catch {
    // Dann bleibt es eben an.
  }
}

/** Schaltet um - fuer den Knopf in der Oberflaeche. */
export async function toggleFullscreen(): Promise<void> {
  if (isFullscreen()) {
    await exitFullscreen();
    return;
  }
  await enterFullscreen();
}

/**
 * Soll beim ersten Antippen von selbst ins Vollbild gewechselt werden
 * (Rueckmeldung vom 15.10.2026)?
 *
 * Bis dahin passierte das erst beim MATCHSTART. Robin am Handy: "ich will
 * den Fullscreen schon beim ersten Menue haben". Genau das geht nur so -
 * Browser erlauben Vollbild ausschliesslich aus einer Eingabe heraus. Beim
 * Oeffnen der Seite laesst es sich also nicht anfragen; die erste Beruehrung
 * ist der frueheste erlaubte Augenblick, und die passiert im Menue ohnehin
 * sofort.
 *
 * Vier Bedingungen, jede mit einem Grund:
 *
 *   - Nur, wenn der Browser es kann. Sonst fragte jeder einzelne Tipp auf
 *     dem iPhone erneut nach etwas, das dort nie erlaubt wird (15.10.2026).
 *   - Nur mit dem Finger. Am Rechner waere ein Vollbild beim ersten Klick
 *     eine Ueberraschung; dort gibt es den Knopf.
 *   - Nicht, wenn es schon an ist. Sonst fragte jeder Tipp erneut.
 *   - Nicht mehr, wenn es schon einmal GEKLAPPT hat. Wer das Vollbild
 *     bewusst verlaesst, soll nicht beim naechsten Tipp wieder
 *     hineingezogen werden.
 *
 * Gemerkt wird der ERFOLG und nicht der Versuch (15.10.2026). Ein
 * fehlgeschlagener Versuch - etwa weil die Eingabe dem Browser nicht
 * genuegte - wuerde sonst den einen erlaubten Versuch aufbrauchen, und
 * danach ginge gar nichts mehr.
 */
export function vollbildBeimErstenTipp(stand: {
  /** Kann der Browser Vollbild? Auf dem iPhone nicht. */
  moeglich: boolean;
  /** Wird das Geraet mit dem Finger bedient? */
  tippflaeche: boolean;
  schonImVollbild: boolean;
  /** Hat es in diesem Besuch schon einmal geklappt? */
  schonGeklappt: boolean;
}): boolean {
  if (!stand.moeglich) return false;
  if (!stand.tippflaeche) return false;
  if (stand.schonImVollbild) return false;

  return !stand.schonGeklappt;
}
